import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { MapPin, Phone, Mail, Send, ArrowRight, CheckCircle } from 'lucide-react'

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    interest: 'b2b'
  })

  const [isSubmitted, setIsSubmitted] = useState(false)

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 },
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData)
    setIsSubmitted(true)
    setTimeout(() => setIsSubmitted(false), 3000)
  }

  return (
    <section id="contact" className="section-padding bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="container-max">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6">
              Join the Energy{' '}
              <span className="bg-gradient-to-r from-energy-yellow to-energy-orange bg-clip-text text-transparent">
                Revolution
              </span>
            </h2>
            <p className="text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to transform your energy infrastructure? Get in touch with our team to explore partnership opportunities 
              and custom solutions for your specific needs.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <motion.div variants={itemVariants} className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold mb-6">Get in Touch</h3>
                <p className="text-gray-300 mb-8">
                  We're here to answer your questions about Pigmentium™ technology and discuss how it can 
                  benefit your energy projects. Reach out through any of the channels below.
                </p>
              </div>

              {/* Contact Details */}
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Headquarters</h4>
                    <p className="text-gray-300 text-sm">
                      Sanayi Mahallesi Teknopark Bulvarı<br />
                      No:1/4C, Z08, Pendik / İstanbul<br />
                      Turkey
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Phone</h4>
                    <p className="text-gray-300 text-sm">
                      <a href="tel:+908503097307" className="hover:text-white transition-colors">
                        +90 850 309 7307
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-energy-yellow to-energy-orange rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-2">Email</h4>
                    <p className="text-gray-300 text-sm">
                      <a href="mailto:info@gges.com.tr" className="hover:text-white transition-colors">
                        info@gges.com.tr
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Cards */}
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/15 transition-colors duration-300">
                  <div className="text-3xl mb-3">🤝</div>
                  <h4 className="font-bold mb-2">Partnerships</h4>
                  <p className="text-xs text-gray-300">Strategic alliances</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/15 transition-colors duration-300">
                  <div className="text-3xl mb-3">💡</div>
                  <h4 className="font-bold mb-2">Innovation</h4>
                  <p className="text-xs text-gray-300">Technology licensing</p>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div variants={itemVariants}>
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <h3 className="text-2xl font-bold mb-6">Send us a Message</h3>
                
                {isSubmitted ? (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                    <h4 className="text-xl font-bold mb-2">Thank You!</h4>
                    <p className="text-gray-300">Your message has been sent successfully. We'll get back to you soon.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Interest Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-3">
                        I'm interested in:
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="interest"
                            value="b2b"
                            checked={formData.interest === 'b2b'}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className={`w-full p-3 rounded-lg border-2 text-center cursor-pointer transition-colors ${
                            formData.interest === 'b2b' 
                              ? 'border-energy-yellow bg-energy-yellow/10 text-energy-yellow' 
                              : 'border-gray-600 text-gray-300 hover:border-gray-500'
                          }`}>
                            <span className="text-sm font-medium">B2B Solutions</span>
                          </div>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="interest"
                            value="b2c"
                            checked={formData.interest === 'b2c'}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className={`w-full p-3 rounded-lg border-2 text-center cursor-pointer transition-colors ${
                            formData.interest === 'b2c' 
                              ? 'border-energy-yellow bg-energy-yellow/10 text-energy-yellow' 
                              : 'border-gray-600 text-gray-300 hover:border-gray-500'
                          }`}>
                            <span className="text-sm font-medium">Consumer Products</span>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Name and Email */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-energy-yellow transition-colors"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-energy-yellow transition-colors"
                          placeholder="your.email@company.com"
                        />
                      </div>
                    </div>

                    {/* Company */}
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                        Company/Organization
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-energy-yellow transition-colors"
                        placeholder="Your company name"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-energy-yellow transition-colors resize-none"
                        placeholder="Tell us about your project or question..."
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      className="w-full bg-energy-gradient text-white font-semibold py-4 px-8 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 active:scale-95 flex items-center justify-center group"
                    >
                      Send Message
                      <Send className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div variants={itemVariants} className="text-center mt-16">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold mb-4">
                Ready to Power the Future?
              </h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Join leading companies and organizations already implementing Pigmentium™ technology 
                to create sustainable, 24/7 energy solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="btn-primary group">
                  Schedule a Demo
                  <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                </button>
                <button className="bg-transparent border-2 border-white text-white font-semibold py-4 px-8 rounded-full transition-all duration-300 hover:bg-white hover:text-gray-900">
                  Download Whitepaper
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default Contact 